import { NotificationsService } from './../../services/notifications/notifications.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewChildren,
  QueryList,
  ChangeDetectorRef
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { SearchService } from '../../services/search/search.service';
import { SpeechService } from '../../services/speech/speech.service';
import { SearchCompletion } from '../../services/search/search-completion';
import { SideMenuService } from '../side-menu/side-menu.service';
import { HeaderItem } from './header-item';
import { HeaderItemLocation } from './header-item-location';
import { HeaderService } from './header.service';
import { Notification } from '../../services/notifications/notification';
import { SoundDefinition } from './sound-definition';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AppStateService } from '@pcplus/core';
import { MatToolbar } from '@angular/material/toolbar';
import { FlexModule } from '@angular/flex-layout/flex';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatBadge } from '@angular/material/badge';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';

@Component({
    selector: 'pcp-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [MatToolbar, FlexModule, ExtendedModule, NgIf, MatIconButton, MatIcon, MatButton, MatTooltip, NgClass, MatMenuTrigger, MatBadge, MatFormField, MatLabel, MatInput, FormsModule, MatAutocompleteTrigger, MatPrefix, MatSuffix, MatAutocomplete, NgFor, MatOption, MatMenu, MatMenuItem, AsyncPipe]
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    public searchService: SearchService,
    public speech: SpeechService,
    public side: SideMenuService,
    private state: AppStateService,
    public breakpointObserver: BreakpointObserver,
    public header: HeaderService,
    private cd: ChangeDetectorRef,
    private zone: NgZone,
    private notifications: NotificationsService
  ) {
    this.completionOptions$ = searchService.completionOptions$;
  }

  private _showSearchBox = false;
  private startItems = new Array<HeaderItem>();
  private middleItems = new Array<HeaderItem>();
  private endItems = new Array<HeaderItem>();
  private onDestroy$ = new Subject<void>();
  currentModule: string | null = null;

  public completionOptions$: Observable<SearchCompletion[]>;
  public middleItemsCount = 0;
  public minimal = true;
  public selectedNotificationSound: string | null = null;

  @ViewChild('startItems', { read: ViewContainerRef, static: true })
  startItemsContainer: ViewContainerRef | null = null;

  @ViewChild('middleItems', { read: ViewContainerRef, static: true })
  middleItemsContainer: ViewContainerRef | null = null;

  @ViewChild('endItems', { read: ViewContainerRef, static: true })
  endItemsContainer: ViewContainerRef | null = null;

  private soundsMenu: MatMenuTrigger | null = null;

  @ViewChildren(MatMenuTrigger)
  set triggers(value: QueryList<MatMenuTrigger>) {
    if (value) {
      this.soundsMenu =
        value.find(
          (q) =>
            (q.menu?.templateRef.elementRef.nativeElement as HTMLElement)
              .parentElement?.id === 'soundsMenu'
        ) || null;
    }
  }

  public searchText: string | null = null;
  public searchPlaceHolder = '';
  public notifications$: Observable<Notification[]> | null = null;
  public newNotifications$: Observable<number | null> | null = null;
  private audio = new Audio();

  get showSearchBox() {
    return this._showSearchBox;
  }

  set showSearchBox(showSearchBox: boolean) {
    this._showSearchBox = showSearchBox;
    if (showSearchBox) {
      setTimeout(() => {
        if (this.searchInput) {
          (this.searchInput.nativeElement as HTMLElement).focus();
        }
      }, 0);
    }
  }

  @ViewChild('searchInput', { read: ElementRef })
  searchInput: ElementRef | null = null;

  ngOnInit() {
    this.selectedNotificationSound = localStorage.getItem(
      'selectedNotificationSound'
    );
    this.state
      .observeCurrentModuleName()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((module) => {
        setTimeout(() => {
          this.currentModule = module;
        }, 0);
      });
  }

  openSoundsMenu($event: MouseEvent) {
    if (this.soundsMenu) {
      $event.preventDefault();
      this.audio.pause();
      this.soundsMenu.openMenu();
    }
  }

  async chooseSound(sound: SoundDefinition | null) {
    this.audio.pause();
    if (sound) {
      this.audio.src = sound.source;
      this.audio.crossOrigin = 'anonymous';
      await this.audio.play();
      this.selectedNotificationSound = sound.source;
      localStorage.setItem('selectedNotificationSound', sound.source);
    } else {
      this.selectedNotificationSound = '';
      localStorage.setItem('selectedNotificationSound', '');
    }
  }

  ngAfterViewInit() {
    this.header.addItem$.pipe(takeUntil(this.onDestroy$)).subscribe((item) => {
      let container: ViewContainerRef | null = null;
      let items: HeaderItem[];
      switch (item.location) {
        case HeaderItemLocation.start:
          container = this.startItemsContainer;
          items = this.startItems;
          break;
        case HeaderItemLocation.middle:
          this.middleItemsCount++;
          container = this.middleItemsContainer;
          items = this.middleItems;
          break;
        case HeaderItemLocation.end:
        default:
          container = this.endItemsContainer;
          items = this.endItems;
          break;
      }
      items.push(item);
      items.sort((a, b) => a.priority - b.priority);
      container!.createEmbeddedView(
        item.templateRef,
        undefined,
        items.indexOf(item)
      );
    });
    this.header.removeItem$.pipe(takeUntil(this.onDestroy$)).subscribe((id) => {
      let container: ViewContainerRef | null = null;
      let index: number;
      let items: HeaderItem[] | null = null;
      index = this.startItems.findIndex((i) => i.id === id);
      if (index !== -1) {
        container = this.startItemsContainer;
        items = this.startItems;
      } else {
        index = this.middleItems.findIndex((i) => i.id === id);
        if (index !== -1) {
          container = this.middleItemsContainer;
          items = this.middleItems;
          this.middleItemsCount--;
        } else {
          index = this.endItems.findIndex((i) => i.id === id);
          if (index !== -1) {
            container = this.endItemsContainer;
            items = this.endItems;
          }
        }
      }
      if (index !== -1 && container && items) {
        container.get(index)?.destroy();
        // container.detach(index);
        // container.remove(index);
        items.splice(index, 1);
      }
    });
    this.searchService.toggleSearch$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        () =>
          (this.showSearchBox =
            !this.showSearchBox && this.searchService.enableSearch)
      );
    this.searchService
      .registerSearchClient('all')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((s) => (this.searchText = s));
    this.notifications$ = this.notifications.getNotifications();
    this.newNotifications$ = this.notifications.getNewNotifications();
    this.notifications$.pipe(takeUntil(this.onDestroy$)).subscribe((ns) => {
      if (
        this.selectedNotificationSound &&
        ns &&
        ns.some((n) => n.isNew && n.soundAlert && !n.isRead)
      ) {
        this.audio.src = this.selectedNotificationSound;
        this.audio.crossOrigin = 'anonymous';
        this.audio.play().then(
          () => {},
          (r) => console.log(r)
        );
      }
    });
    this.cd.detectChanges();
  }

  checkAlerts() {
    this.notifications.checkAlerts();
  }

  performCompletionAction(completion: SearchCompletion) {
    if (completion.optionSelected) {
      completion.optionSelected(
        completion.value ? completion.value : completion.text
      );
    }
  }
  search(searchStr: string) {
    this.searchService.search(searchStr);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  searchByVoice() {
    this.zone.run(() => {
      this.searchPlaceHolder = $localize`Start speaking now`;
      this.searchText = '';
      this.speech.words$.pipe(take(1)).subscribe((result) => {
        this.zone.run(() => {
          this.searchText = result;
          this.searchService.search(this.searchText);
          this.searchPlaceHolder = '';
        });
      });
      this.speech.startListening();
    });
  }
}
